// ═══════════════════════════════════════════════════════════════════════════
// RUN 20: Consistent Header Check
// ═══════════════════════════════════════════════════════════════════════════
import { safeAlert, reportResult } from '../runs/common';
import { getConfig, ConsistentHeaderConfig } from '../taskpane/configService';

// ── Types ──────────────────────────────────────────────────────────────────

interface PageNumberEntry {
  label: string;
  pageNum: number;
  totalPages: number | null;
}

interface Patterns {
  MONTH_PATTERN: RegExp;
  YEAR_PATTERN:  RegExp;
  PAGE_PATTERN:  RegExp;
  PAGE_TEST:     RegExp;
  PAGE_CAPTURE:  RegExp;
}

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * S7735: positive-first — checks whether a paragraph contributes body text
 * (i.e. contains no month, page, or year tokens).
 */
function isBodyText(text: string, p: Patterns): boolean {
  const hasMonth = p.MONTH_PATTERN.test(text);
  const hasPage  = p.PAGE_TEST.test(text);

  p.YEAR_PATTERN.lastIndex = 0;
  const hasYear = p.YEAR_PATTERN.test(text);
  p.YEAR_PATTERN.lastIndex = 0;

  // S7735: positive-first — bail out when any header token is present,
  // return true (is body text) only when none are found.
  if (hasMonth || hasPage || hasYear) {
    return false;
  }
  return true;
}

/**
 * Parses the optional "of Y" group from a PAGE_CAPTURE match array.
 * Extracted to reduce Cognitive Complexity of capturePageNumber (S3776).
 */
function parseTotalPages(match: RegExpExecArray): number | null {
  if (match[2] != null) {
    return Number.parseInt(match[2], 10);
  }
  return null;
}

/**
 * Attempts to parse a "Page X [of Y]" entry from a paragraph.
 * Returns null when the pattern is absent.
 * S7773: uses Number.parseInt.
 * S6594: uses RegExp.exec() instead of String.match().
 * S3776: totalPages parsing extracted to parseTotalPages().
 */
function capturePageNumber(
  text: string,
  label: string,
  p: Patterns,
): PageNumberEntry | null {
  const hasPage = p.PAGE_TEST.test(text);
  if (!hasPage) return null;

  const match = p.PAGE_CAPTURE.exec(text);   // S6594: RegExp.exec()
  if (!match) return null;

  return {
    label,
    pageNum:    Number.parseInt(match[1], 10),
    totalPages: parseTotalPages(match),        // S3776: extracted helper
  };
}

/**
 * Strips page and year tokens from raw header text to produce a
 * normalisation key for the consistency dictionary.
 * S7735: positive-first guard on empty key.
 */
function buildHeaderKey(rawText: string, p: Patterns): string | null {
  const normalised = rawText
    .replace(p.PAGE_PATTERN, "")
    .replace(p.YEAR_PATTERN, "")
    .trim();

  p.YEAR_PATTERN.lastIndex = 0;
  p.PAGE_PATTERN.lastIndex = 0;

  // S7735: return null when empty rather than checking !normalised at call-site
  return normalised.length > 0 ? normalised : null;
}

/**
 * Iterates every paragraph in one header body, accumulates body text into
 * `textbox`, and appends any page-number entries to `pageNumbers`.
 *
 * Extracted from collectHeaderTexts to reduce Cognitive Complexity (S3776).
 */
async function processHeaderParagraphs(
  context: Word.RequestContext,
  headerBody: Word.Body,
  sectionLabel: string,
  p: Patterns,
  pageNumbers: PageNumberEntry[],
): Promise<string> {
  const paragraphs = headerBody.paragraphs;
  paragraphs.load("items");
  await context.sync();

  if (paragraphs.items.length === 0) {
    console.log(`  ${sectionLabel}: Empty header — skipped`);
    return "";
  }

  let textbox = "";

  for (let pi = 0; pi < paragraphs.items.length; pi++) {
    const para = paragraphs.items[pi];
    para.load("text");
    await context.sync();

    const paraText = para.text ?? "";
    const label    = `${sectionLabel} | Para ${pi + 1}`;

    if (isBodyText(paraText, p)) {
      textbox += paraText;
    }

    const entry = capturePageNumber(paraText, label, p);
    if (entry) pageNumbers.push(entry);
  }

  return textbox;
}

/**
 * Derives the normalisation key from `textbox` and upserts it into
 * `headerDict`.
 *
 * Extracted from collectHeaderTexts to reduce Cognitive Complexity (S3776).
 * Returns false when the entry was skipped (empty key or empty textbox).
 */
function registerHeaderKey(
  textbox: string,
  sectionLabel: string,
  p: Patterns,
  headerDict: Record<string, string[]>,
): boolean {
  if (textbox.length === 0) {
    console.log(`  ${sectionLabel}: No qualifying text — skipped`);
    return false;
  }

  const key = buildHeaderKey(textbox, p);
  if (key === null) {
    console.log(`  ${sectionLabel}: Key empty after normalisation — skipped`);
    return false;
  }

  if (key in headerDict) {
    headerDict[key].push(textbox);
  } else {
    headerDict[key] = [textbox];
  }

  console.log(`  ${sectionLabel}: key="${key.substring(0, 60)}"`);
  return true;
}

/**
 * Iterates every section × header-type combination and populates
 * `headerDict` and `pageNumbers` in-place.
 *
 * S3776: inner paragraph loop extracted to processHeaderParagraphs();
 *        dict-update logic extracted to registerHeaderKey().
 * S2486: the inner catch logs the error instead of swallowing it.
 */
async function collectHeaderTexts(
  context: Word.RequestContext,
  sections: Word.SectionCollection,
  headerTypes: { type: Word.HeaderFooterType; name: string }[],
  p: Patterns,
  headerDict: Record<string, string[]>,
  pageNumbers: PageNumberEntry[],
): Promise<void> {
  const totalSections = sections.items.length;

  for (let s = 0; s < totalSections; s++) {
    const section = sections.items[s];

    for (const ht of headerTypes) {
      const sectionLabel = `Section ${s + 1} ${ht.name}`;
      let headerBody: Word.Body;

      try {
        headerBody = section.getHeader(ht.type);
        headerBody.load("paragraphs");
        await context.sync();
      } catch (e) {
        // S2486: log the error rather than silently ignoring it
        console.log(`  ${sectionLabel}: No header — skipped (${(e as Error).message})`);
        continue;
      }

      const textbox = await processHeaderParagraphs(
        context, headerBody, sectionLabel, p, pageNumbers,
      );

      registerHeaderKey(textbox, sectionLabel, p, headerDict);
    }
  }
}

/**
 * Inserts the inconsistency comment on the first body paragraph.
 * Extracted to cut Cognitive Complexity (S3776).
 * S7735: positive-first — normal path in the if-branch, error path in else.
 */
async function insertInconsistencyComment(
  context: Word.RequestContext,
  commentText: string,
  headerDict: Record<string, string[]>,
): Promise<void> {
  const body = context.document.body;
  body.load("paragraphs");
  await context.sync();

  const paragraphs = body.paragraphs;
  paragraphs.load("items");
  await context.sync();

  // S7735: positive-first — handle the normal (non-empty) path first,
  // fall to the else-branch only when no paragraphs exist.
  if (paragraphs.items.length > 0) {
    const firstParaRange = paragraphs.items[0].getRange();
    firstParaRange.load("text");
    await context.sync();

    firstParaRange.insertComment(commentText);
    await context.sync();

    console.log(`  Comment inserted: "${commentText}"`);

    let variantIndex = 1;
    for (const [key, values] of Object.entries(headerDict)) {
      console.log(`  Variant ${variantIndex++}: "${key.substring(0, 80)}" (seen ${values.length}x)`);
    }
  } else {
    console.log("⚠ No paragraphs in document body — cannot insert comment");
    safeAlert("Consistent Header Check\n\nInconsistent headers found but no paragraph available for comment.");
  }
}

// ── Entry point ────────────────────────────────────────────────────────────

export async function run20() {
  // ── Read config ──
  const cfg = getConfig();
  // S4325: removed unnecessary type assertion; ConsistentHeaderConfig is already the declared type
  const c: ConsistentHeaderConfig = cfg.run20_consistentHeader;

  if (!c.enforceConsistency) {
    console.log("Consistent Header check disabled in config — skipped");
    return;
  }

  // ── Build patterns from config strings ──
  const patterns: Patterns = {
    MONTH_PATTERN: new RegExp(c.monthPattern, "i"),
    YEAR_PATTERN:  new RegExp(c.yearPattern,  "g"),
    PAGE_PATTERN:  new RegExp(c.pagePattern,  "gi"),
    PAGE_TEST:     new RegExp(c.pagePattern,  "i"),
    PAGE_CAPTURE:  new RegExp(c.pageCapturePattern, "i"),
  };

  // ── Build header-type list from config ──
  const typeMap: Record<string, { type: Word.HeaderFooterType; name: string }> = {
    primary:   { type: Word.HeaderFooterType.primary,   name: "Primary"   },
    firstPage: { type: Word.HeaderFooterType.firstPage, name: "FirstPage" },
    evenPages: { type: Word.HeaderFooterType.evenPages, name: "EvenPages" },
  };
  const headerTypes = c.headerTypesToCheck.map(k => typeMap[k]).filter(Boolean);

  await Word.run(async (context) => {
    try {
      console.log("═".repeat(70));
      console.log("RUN 20: Consistent Header Check");
      console.log("═".repeat(70));

      const sections = context.document.sections;
      sections.load("items");
      await context.sync();

      console.log(`Total sections: ${sections.items.length}\n`);

      const headerDict: Record<string, string[]> = {};
      const pageNumbers: PageNumberEntry[]        = [];

      // ── Step 1: Collect header texts across all sections ──
      await collectHeaderTexts(context, sections, headerTypes, patterns, headerDict, pageNumbers);

      // ── Debug: print all captured page numbers ──
      console.log("\n" + "─".repeat(50));
      console.log("PAGE NUMBERS FOUND IN HEADERS (debug — API limitation: same value per section)");
      console.log("─".repeat(50));

      // S7735: positive-first — list entries when present, fall to the none-found message
      if (pageNumbers.length > 0) {
        pageNumbers.forEach((entry, i) => {
          const total = entry.totalPages == null ? "" : ` of ${entry.totalPages}`;
          console.log(`  [${i + 1}] ${entry.label} → Page ${entry.pageNum}${total}`);
        });
      } else {
        console.log("  (none — no 'Page X [of Y]' text detected in any header)");
      }
      console.log("─".repeat(50) + "\n");

      const uniqueCount = Object.keys(headerDict).length;
      console.log(`Unique header variants found: ${uniqueCount}`);

      // ── Step 2: Flag inconsistencies ──
      // S7735: positive-first — consistent (normal) outcome leads; error path in else
      if (uniqueCount <= 1) {
        console.log("✓ All headers are consistent");
        reportResult('Consistent Header — no changes needed', [
          { label: 'All headers are consistent', note: 'No changes made' },
        ]);
      } else {
        console.log("⚠ Inconsistent headers detected — inserting comment");
        await insertInconsistencyComment(context, c.inconsistencyCommentText, headerDict);
        reportResult('Consistent Header Complete', [
          { label: 'Header inconsistencies found and flagged' },
        ]);
      }

      console.log("\n" + "═".repeat(70));
      console.log("✓ Consistent Header check complete!");
      console.log("═".repeat(70));

    } catch (error) {
      console.error("Error in run20:", error);
      console.error("Message:", (error as Error).message);
      safeAlert("An error occurred during Consistent Header check.\nCheck console (F12) for details.");
    }
  });
}